/* assets/gift-wrap.js
   OCU Slide Cart Drawer compatible

   - Checkbox controls cart attribute: gift_wrap = true/false
   - Gift is NOT added on checkbox click — only after real cart activity
   - Gift quantity always equals total quantity of NON-gift items in cart
   - Removing/changing qty of main items updates gift immediately
   - Opens OCU drawer after add-to-cart, but never on checkbox toggle
*/

(function () {
  const CART_ATTR_KEY = "gift_wrap";
  const GIFT_LINE_PROP_KEY = "_gift_wrap";

  let syncTimer = null;
  let internalCartWrite = 0;

  // -------------------------
  // OCU drawer helpers
  // -------------------------
  function openOcuCartDrawer() {
    // Zipify Help Center: open drawer
    dispatchEvent(new CustomEvent("ocu:cart:open"));
  }

  function refreshOcuDrawer() {
    // OCU uses these events internally to refresh drawer state (gift manager code)
    // Dispatch twice (immediate + 1s) to ensure UI re-renders even if mid-transition.
    dispatchEvent(new CustomEvent("ocu:cart:update", { detail: { gift: true, appropriate: false } }));
    setTimeout(() => {
      dispatchEvent(new CustomEvent("ocu:cart:update", { detail: { gift: true, appropriate: false } }));
    }, 1000);
  }

  function isOcuDrawerOpen() {
    // Best-effort detection: if their drawer root exists in DOM, treat as open.
    // (Even if this selector changes later, refreshOcuDrawer() is harmless.)
    return !!document.querySelector(".ocu-cart-drawer, .ocu-slide-cart, [data-ocu-cart-drawer]");
  }

  // -------------------------
  // DOM helpers
  // -------------------------
  function getCheckbox() {
    return document.querySelector("#GiftWrapCheckbox");
  }

  function getGiftVariantIdFromDom() {
    const cb = getCheckbox();
    const raw = cb?.getAttribute("data-gift-variant-id");
    return raw ? Number(raw) : null;
  }

  // -------------------------
  // Cart API helpers
  // -------------------------
  async function fetchCart() {
    const r = await fetch("/cart.js", { credentials: "same-origin" });
    if (!r.ok) throw new Error("Failed to fetch cart");
    return r.json();
  }

  async function setCartGiftAttr(enabled) {
    internalCartWrite++;
    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          attributes: { [CART_ATTR_KEY]: enabled ? "true" : "false" },
        }),
      });
    } finally {
      internalCartWrite--;
    }
  }

  async function changeLineByKey(lineKey, qty) {
    internalCartWrite++;
    try {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ id: lineKey, quantity: qty }),
      });
    } finally {
      internalCartWrite--;
    }
  }

  async function addGiftLine(giftVariantId, qty) {
    internalCartWrite++;
    try {
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          items: [
            {
              id: giftVariantId,
              quantity: qty,
              properties: { [GIFT_LINE_PROP_KEY]: "true" },
            },
          ],
        }),
      });
    } finally {
      internalCartWrite--;
    }
  }

  function calcNonGiftQty(cart, giftVariantId) {
    return (cart.items || []).reduce((sum, item) => {
      if (giftVariantId && item.variant_id === giftVariantId) return sum;
      return sum + (item.quantity || 0);
    }, 0);
  }

  function findGiftLine(cart, giftVariantId) {
    if (!giftVariantId) return null;
    return (cart.items || []).find((i) => i.variant_id === giftVariantId) || null;
  }

  // -------------------------
  // Core sync: keep gift qty == non-gift qty
  // Returns true if we changed the cart (added/removed/adjusted gift)
  // -------------------------
  async function syncGiftWrap() {
    const giftVariantId = getGiftVariantIdFromDom();
    if (!giftVariantId) return false;

    const cart = await fetchCart();
    const enabled = cart.attributes?.[CART_ATTR_KEY] === "true";

    const nonGiftQty = calcNonGiftQty(cart, giftVariantId);
    const giftLine = findGiftLine(cart, giftVariantId);

    // Disabled -> ensure gift removed
    if (!enabled) {
      if (giftLine) {
        await changeLineByKey(giftLine.key, 0);
        return true;
      }
      return false;
    }

    // Enabled but no other items -> remove gift (no gift-only carts)
    if (nonGiftQty <= 0) {
      if (giftLine) {
        await changeLineByKey(giftLine.key, 0);
        return true;
      }
      return false;
    }

    // Enabled and should exist:
    if (!giftLine) {
      await addGiftLine(giftVariantId, nonGiftQty);
      return true;
    }

    if (giftLine.quantity !== nonGiftQty) {
      await changeLineByKey(giftLine.key, nonGiftQty);
      return true;
    }

    return false;
  }

  function scheduleSync({ openDrawer = false } = {}) {
    if (syncTimer) clearTimeout(syncTimer);

    syncTimer = setTimeout(async () => {
      try {
        const changed = await syncGiftWrap();

        // If we changed the cart and the OCU drawer is on-screen, force a redraw.
        // This is what fixes “needs close/reopen” after qty changes/removals.
        if (changed && isOcuDrawerOpen()) {
          refreshOcuDrawer();
        }

        if (openDrawer) {
          openOcuCartDrawer();
        }
      } catch (e) {
        console.warn("[GiftWrap] sync failed", e);
      }
    }, 50);
  }

  // -------------------------
  // Hook ALL AJAX cart updates (covers OCU + theme)
  // -------------------------
  function patchFetch() {
    if (!window.fetch || window.fetch.__giftWrapPatched) return;

    const originalFetch = window.fetch;

    window.fetch = async function (input, init) {
      const url = typeof input === "string" ? input : input?.url || "";
      const method = (init?.method || (typeof input !== "string" ? input?.method : "") || "GET").toUpperCase();

      const isCartWrite =
        method !== "GET" &&
        (url.includes("/cart/add") || url.includes("/cart/change") || url.includes("/cart/update"));

      const res = await originalFetch.apply(this, arguments);

      // If cart write came from OUR own sync ops, ignore to prevent loops
      if (isCartWrite && internalCartWrite === 0) {
        const openDrawer = url.includes("/cart/add");
        // Any add/change/update triggers a sync; add opens drawer; change/update does not.
        scheduleSync({ openDrawer });
      }

      return res;
    };

    window.fetch.__giftWrapPatched = true;
  }

  // -------------------------
  // Checkbox behavior
  // (never opens drawer)
  // -------------------------
  function bindCheckboxChange() {
    const cb = getCheckbox();
    if (!cb) return;

    cb.addEventListener("change", async () => {
      try {
        await setCartGiftAttr(cb.checked);
        // Do not open drawer here
        scheduleSync({ openDrawer: false });
      } catch (e) {
        console.warn("[GiftWrap] checkbox change failed", e);
      }
    });
  }

  // -------------------------
  // Init
  // -------------------------
  document.addEventListener("DOMContentLoaded", () => {
    patchFetch();
    bindCheckboxChange();

    // Keep cart consistent on load (no drawer opening)
    scheduleSync({ openDrawer: false });
  });
})();
